import useScrollAnimation from "../../../hooks/useScrollAnimation";

const flowSteps = [
  { icon: "ri-radar-line", label: "LEAD CAPTURE", ping: "node-ping" },
  { icon: "ri-equalizer-line", label: "QUALIFICATION", ping: "node-ping-2" },
  { icon: "ri-calendar-check-line", label: "BOOKING", ping: "node-ping-3" },
  { icon: "ri-send-plane-line", label: "FOLLOW-UP", ping: "node-ping-4" },
  { icon: "ri-shield-check-line", label: "RETENTION", ping: "node-ping-5" },
];

const SystemFlowSection = () => {
  const sectionRef = useScrollAnimation<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="bg-[#1A1A1A] py-24 md:py-36 px-6 md:px-16 relative overflow-hidden"
    >
      {/* Background grid */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.04]">
          <defs>
            <pattern id="flowGrid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#FFD100" strokeWidth="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#flowGrid)" />
        </svg>
      </div>

      {/* Center radial glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 600,
          height: 300,
          background: "radial-gradient(ellipse at center, rgba(255,209,0,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="reveal">
            <span
              className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4"
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              PATIENT JOURNEY ARCHITECTURE
            </span>
            <h2
              className="text-white font-black uppercase text-4xl md:text-5xl"
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
            >
              THE SIGNAL PATH
            </h2>
          </div>
        </div>

        {/* Flow Diagram */}
        <div className="reveal flex flex-col md:flex-row items-center justify-center">
          {flowSteps.map((step, i) => (
            <div key={i} className="flex flex-col md:flex-row items-center">
              {/* Node */}
              <div className="flex flex-col items-center gap-5 group">
                {/* Pulse ring container */}
                <div className="relative flex items-center justify-center">
                  {/* Outer pulse ring */}
                  <div
                    className={`${step.ping} absolute border border-[#FFD100]/40 rounded-full`}
                    style={{ width: 80, height: 80 }}
                  />
                  {/* Main node circle */}
                  <div
                    className="relative z-10 w-[72px] h-[72px] rounded-full border-2 border-[#FFD100] bg-black flex items-center justify-center transition-all duration-300 group-hover:bg-[#FFD100]/10"
                    style={{ boxShadow: "0 0 20px rgba(255,209,0,0.10)" }}
                  >
                    <i className={`${step.icon} text-[#FFD100] text-2xl`} />
                  </div>
                </div>
                <span
                  className="text-white text-[10px] font-black tracking-[2px] text-center whitespace-nowrap"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector */}
              {i < flowSteps.length - 1 && (
                <div className="flex items-center justify-center w-14 md:w-16 h-14 md:h-auto shrink-0">
                  {/* Vertical on mobile */}
                  <div className="md:hidden flex flex-col items-center gap-0.5">
                    {[...Array(4)].map((_, di) => (
                      <div key={di} className="w-px h-2 bg-[#FFD100]/30" />
                    ))}
                    <i className="ri-arrow-down-s-line text-[#FFD100]/50 text-xs" />
                  </div>
                  {/* Horizontal on desktop */}
                  <div className="hidden md:flex items-center gap-0.5 w-full">
                    {[...Array(6)].map((_, di) => (
                      <div key={di} className="h-px flex-1 bg-[#FFD100]/25" />
                    ))}
                    <i className="ri-arrow-right-s-line text-[#FFD100]/60 text-sm" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Description */}
        <div className="reveal delay-300 text-center mt-16">
          <p
            className="text-[#E5E5E5] text-sm max-w-xl mx-auto"
            style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.8" }}
          >
            Automated patient journey from first contact to long-term retention. Every
            touchpoint engineered. Every handoff automated.
          </p>
        </div>

        {/* Bottom decorative metrics bar */}
        <div className="reveal delay-400 mt-16 grid grid-cols-3 gap-px bg-[#FFD100]/10 max-w-2xl mx-auto">
          {[
            { val: "5 min", label: "RESPONSE TIME" },
            { val: "14 days", label: "DEPLOYMENT" },
            { val: "100%", label: "AUTOMATED" },
          ].map((stat, i) => (
            <div key={i} className="bg-[#1A1A1A] px-6 py-5 text-center">
              <div
                className="text-[#FFD100] font-black text-xl mb-1"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {stat.val}
              </div>
              <div
                className="text-[#E5E5E5]/60 text-[10px] tracking-[2px] font-bold"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SystemFlowSection;
